import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { 
  MessageCircle, Users, Plus, Send, ArrowLeft, Shield, 
  Crown, Tag, AlertTriangle, X, Check, Loader2, ShoppingBag,
  Flag, ExternalLink, Lock, Unlock, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ChatRooms() {
  const navigate = useNavigate();
  const { roomId } = useParams();
  const [user, setUser] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [terms, setTerms] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newRoom, setNewRoom] = useState({ name: '', description: '', rules: '', is_private: false });

  // Chat room state
  const [currentRoom, setCurrentRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Report state
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');
  const [reportDetails, setReportDetails] = useState('');

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    } else {
      navigate('/');
      return;
    }
    checkTermsStatus();
  }, [navigate]);

  useEffect(() => {
    if (termsAccepted) {
      loadRooms();
    }
  }, [termsAccepted]);

  useEffect(() => {
    if (roomId && termsAccepted) {
      loadRoom(roomId);
      loadMessages(roomId);
      // Start polling for new messages
      pollIntervalRef.current = setInterval(() => {
        loadMessages(roomId, true);
      }, 3000);
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [roomId, termsAccepted]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const checkTermsStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chat/terms-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTermsAccepted(response.data.accepted);
      if (!response.data.accepted) {
        loadTerms();
      }
    } catch (error) {
      console.error('Error checking terms:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadTerms = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/chat/terms`);
      setTerms(response.data.terms);
      setShowTermsModal(true);
    } catch (error) {
      console.error('Error loading terms:', error);
    }
  };

  const acceptTerms = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/chat/accept-terms`, 
        { accepted: true },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTermsAccepted(true);
      setShowTermsModal(false);
      toast.success('Terms accepted! Welcome to the chat community.');
    } catch (error) {
      toast.error('Failed to accept terms');
    }
  };

  const loadRooms = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chat/rooms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRooms(response.data.rooms);
    } catch (error) {
      console.error('Error loading rooms:', error);
    }
  };

  const loadRoom = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chat/rooms/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCurrentRoom(response.data);
    } catch (error) {
      console.error('Error loading room:', error);
      toast.error('Room not found');
      navigate('/chat');
    }
  };

  const loadMessages = async (id, silent = false) => {
    if (!silent) setLoadingMessages(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/chat/rooms/${id}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages);
    } catch (error) {
      if (!silent) console.error('Error loading messages:', error);
    } finally {
      if (!silent) setLoadingMessages(false);
    }
  };

  // Room rules acceptance state
  const [showRulesModal, setShowRulesModal] = useState(false);
  const [pendingRoomJoin, setPendingRoomJoin] = useState(null);
  const [joiningRoom, setJoiningRoom] = useState(false);

  const joinRoom = async (room, acceptedRules = false) => {
    // If room object passed, store it for rules modal
    if (typeof room === 'object' && !acceptedRules) {
      setPendingRoomJoin(room);
      setShowRulesModal(true);
      return;
    }
    
    const roomId = typeof room === 'object' ? room.room_id : room;
    setJoiningRoom(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/chat/rooms/${roomId}/join?rules_accepted=${acceptedRules}`, 
        {}, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.already_member) {
        navigate(`/chat/${roomId}`);
      } else {
        toast.success('Joined room!');
        navigate(`/chat/${roomId}`);
      }
      setShowRulesModal(false);
      setPendingRoomJoin(null);
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('rules')) {
        // Rules not accepted - show modal
        if (pendingRoomJoin) {
          setShowRulesModal(true);
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to join room');
      }
    } finally {
      setJoiningRoom(false);
    }
  };

  const confirmJoinWithRules = () => {
    if (pendingRoomJoin) {
      joinRoom(pendingRoomJoin, true);
    }
  };

  const cancelJoinRoom = () => {
    setShowRulesModal(false);
    setPendingRoomJoin(null);
  };

  const createRoom = async () => {
    if (!newRoom.name.trim() || !newRoom.rules.trim()) {
      toast.error('Name and rules are required');
      return;
    }
    if (newRoom.rules.length < 20) {
      toast.error('Rules must be at least 20 characters');
      return;
    }

    setCreating(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API_URL}/api/chat/rooms`, newRoom, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Chat room created!');
      setShowCreateModal(false);
      setNewRoom({ name: '', description: '', rules: '', is_private: false });
      loadRooms();
      navigate(`/chat/${response.data.room_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create room');
    } finally {
      setCreating(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    setSending(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/api/chat/rooms/${roomId}/messages`,
        { room_id: roomId, content: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessages(prev => [...prev, response.data.message]);
      setNewMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const openReport = (messageId = null) => {
    setReportTarget({ room_id: roomId, message_id: messageId });
    setShowReportModal(true);
  };

  const submitReport = async () => {
    if (!reportReason.trim()) {
      toast.error('Please select a reason');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/api/chat/report`, {
        room_id: reportTarget.room_id,
        message_id: reportTarget.message_id,
        reason: reportReason,
        details: reportDetails
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Report submitted. Our team will review it.');
      setShowReportModal(false);
      setReportReason('');
      setReportDetails('');
    } catch (error) {
      toast.error('Failed to submit report');
    }
  };

  const getRoomIcon = (room) => {
    if (room.room_type === 'creator_lounge') return <Crown className="w-5 h-5 text-white" />;
    if (room.room_type === 'deals') return <Tag className="w-5 h-5 text-[#00FF94]" />;
    if (room.is_private) return <Lock className="w-5 h-5 text-yellow-400" />;
    return <MessageCircle className="w-5 h-5 text-[#00F0FF]" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00F0FF]" />
      </div>
    );
  }

  // Terms Modal
  if (showTermsModal && !termsAccepted) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex items-center gap-3 mb-6">
            <Shield className="w-8 h-8 text-[#00F0FF]" />
            <div>
              <h2 className="text-2xl font-bold">Chat Community Guidelines</h2>
              <p className="text-sm text-neutral-400">Please read and accept to continue</p>
            </div>
          </div>
          
          <div className="bg-neutral-800/50 rounded-xl p-4 mb-6 max-h-[400px] overflow-y-auto">
            <pre className="whitespace-pre-wrap text-sm text-neutral-300 font-sans">
              {terms}
            </pre>
          </div>
          
          <div className="flex gap-3">
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="flex-1 border-white/20"
            >
              Decline
            </Button>
            <Button
              onClick={acceptTerms}
              className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
            >
              <Check className="w-4 h-4 mr-2" />
              I Accept
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Chat Room View
  if (roomId && currentRoom) {
    return (
      <div className="min-h-screen bg-[#050505] flex flex-col">
        {/* Header */}
        <header className="border-b border-white/10 bg-neutral-900/80 backdrop-blur-xl sticky top-0 z-10">
          <div className="container mx-auto px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/chat')}
                className="text-neutral-400 hover:text-white"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              {getRoomIcon(currentRoom)}
              <div>
                <h1 className="font-bold">{currentRoom.name}</h1>
                <p className="text-xs text-neutral-400">{currentRoom.member_count || 0} members</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openReport()}
                className="text-neutral-400 hover:text-red-400"
                title="Report Room"
              >
                <Flag className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </header>

        {/* Room Rules Banner */}
        {currentRoom.rules && (
          <div className="bg-neutral-900/50 border-b border-white/5 px-4 py-2">
            <details className="text-sm">
              <summary className="cursor-pointer text-neutral-400 hover:text-white flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Room Rules (click to expand)
              </summary>
              <div className="mt-2 p-3 bg-neutral-800/50 rounded-lg text-neutral-300 whitespace-pre-wrap">
                {currentRoom.rules}
              </div>
            </details>
          </div>
        )}

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loadingMessages ? (
            <div className="flex justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-[#00F0FF]" />
            </div>
          ) : messages.length === 0 ? (
            <div className="text-center py-12 text-neutral-500">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No messages yet. Start the conversation!</p>
            </div>
          ) : (
            messages.map((msg) => (
              <div
                key={msg.message_id}
                className={`flex gap-3 ${msg.user_id === user?.user_id ? 'justify-end' : ''}`}
              >
                {msg.user_id !== user?.user_id && (
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#00F0FF] to-white flex items-center justify-center flex-shrink-0">
                    {msg.user_picture ? (
                      <img src={msg.user_picture} alt="" className="w-8 h-8 rounded-full" />
                    ) : (
                      <span className="text-xs font-bold">{msg.user_name?.[0]?.toUpperCase()}</span>
                    )}
                  </div>
                )}
                <div className={`max-w-[70%] ${msg.user_id === user?.user_id ? 'order-first' : ''}`}>
                  <div className={`flex items-center gap-2 mb-1 ${msg.user_id === user?.user_id ? 'justify-end' : ''}`}>
                    <span className="text-sm font-medium">{msg.user_name}</span>
                    {msg.user_role === 'creator' && (
                      <span className="text-xs px-1.5 py-0.5 rounded bg-[#00F0FF]/20 text-[#00F0FF]">Creator</span>
                    )}
                    <span className="text-xs text-neutral-500">
                      {new Date(msg.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </span>
                    {msg.user_id !== user?.user_id && (
                      <button
                        onClick={() => openReport(msg.message_id)}
                        className="text-neutral-600 hover:text-red-400 transition-colors"
                        title="Report message"
                      >
                        <Flag className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className={`rounded-2xl px-4 py-2 ${
                    msg.user_id === user?.user_id
                      ? 'bg-[#00F0FF] text-black'
                      : 'bg-neutral-800'
                  }`}>
                    <p>{msg.content}</p>
                  </div>
                  {/* Shared Product */}
                  {msg.shared_product && (
                    <div 
                      className="mt-2 p-3 bg-neutral-800/80 border border-white/10 rounded-xl cursor-pointer hover:border-[#00F0FF]/50 transition-colors"
                      onClick={() => navigate(`/product/${msg.shared_product.product_id}`)}
                    >
                      <div className="flex items-center gap-3">
                        {msg.shared_product.image && (
                          <img 
                            src={msg.shared_product.image} 
                            alt="" 
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm truncate">{msg.shared_product.name}</p>
                          <p className="text-[#00FF94] font-bold">${msg.shared_product.price}</p>
                        </div>
                        <ExternalLink className="w-4 h-4 text-neutral-400" />
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Message Input */}
        <form onSubmit={sendMessage} className="border-t border-white/10 p-4 bg-neutral-900/80">
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type a message..."
              className="flex-1 bg-neutral-800 border-white/10"
              maxLength={1000}
              disabled={sending}
            />
            <Button
              type="submit"
              disabled={!newMessage.trim() || sending}
              className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </Button>
          </div>
        </form>

        {/* Report Modal */}
        {showReportModal && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 max-w-md w-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-red-400" />
                  Report Content
                </h3>
                <button onClick={() => setShowReportModal(false)} className="text-neutral-400 hover:text-white">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Reason</label>
                  <select
                    value={reportReason}
                    onChange={(e) => setReportReason(e.target.value)}
                    className="w-full bg-neutral-800 border border-white/10 rounded-lg px-3 py-2"
                  >
                    <option value="">Select a reason...</option>
                    <option value="spam">Spam or excessive promotion</option>
                    <option value="harassment">Harassment or bullying</option>
                    <option value="inappropriate">Inappropriate content</option>
                    <option value="scam">Scam or fraud</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium mb-2">Details (optional)</label>
                  <Textarea
                    value={reportDetails}
                    onChange={(e) => setReportDetails(e.target.value)}
                    placeholder="Provide additional context..."
                    className="bg-neutral-800 border-white/10"
                    rows={3}
                  />
                </div>
                
                <Button
                  onClick={submitReport}
                  className="w-full bg-red-500 hover:bg-red-600"
                  disabled={!reportReason}
                >
                  Submit Report
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Rooms List View
  return (
    <div className="min-h-screen bg-[#050505] text-white">
      {/* Header */}
      <header className="border-b border-white/10 bg-neutral-900/80 backdrop-blur-xl sticky top-0 z-10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/')}
              className="text-neutral-400 hover:text-white"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <MessageCircle className="w-6 h-6 text-[#00F0FF]" />
            <h1 className="text-xl font-bold">Chat Rooms</h1>
          </div>
          {(user?.role === 'creator' || user?.role === 'admin') && (
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-white text-black hover:bg-neutral-200"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Room
            </Button>
          )}
        </div>
      </header>

      {/* Rooms List */}
      <div className="container mx-auto px-4 py-6">
        <div className="grid gap-4">
          {rooms.length === 0 ? (
            <div className="text-center py-12 bg-neutral-900/50 rounded-2xl border border-white/5">
              <MessageCircle className="w-12 h-12 mx-auto mb-3 text-neutral-600" />
              <p className="text-neutral-400">No chat rooms available</p>
            </div>
          ) : (
            rooms.map((room) => (
              <div
                key={room.room_id}
                className="bg-neutral-900/50 border border-white/10 rounded-2xl p-5 hover:border-[#00F0FF]/30 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      room.room_type === 'creator_lounge' ? 'bg-white text-black/20' :
                      room.room_type === 'deals' ? 'bg-[#00FF94]/20' :
                      'bg-[#00F0FF]/20'
                    }`}>
                      {getRoomIcon(room)}
                    </div>
                    <div>
                      <h3 className="font-bold text-lg flex items-center gap-2">
                        {room.name}
                        {room.room_type === 'creator_lounge' && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-[#00F0FF]/20 text-[#00F0FF]">
                            Creators Only
                          </span>
                        )}
                        {room.is_private && (
                          <Lock className="w-4 h-4 text-yellow-400" />
                        )}
                      </h3>
                      <p className="text-sm text-neutral-400 mt-1">{room.description}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-neutral-500">
                        <span className="flex items-center gap-1">
                          <Users className="w-3 h-3" />
                          {room.member_count || 0} members
                        </span>
                        {room.creator_name && !room.is_system_room && (
                          <span>by {room.creator_name}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div>
                    {room.is_member ? (
                      <Button
                        onClick={() => navigate(`/chat/${room.room_id}`)}
                        className="bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                      >
                        Open
                      </Button>
                    ) : (
                      <Button
                        onClick={() => joinRoom(room)}
                        variant="outline"
                        className="border-[#00F0FF]/50 text-[#00F0FF] hover:bg-[#00F0FF]/10"
                      >
                        Join
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Create Room Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold flex items-center gap-2">
                <Plus className="w-5 h-5 text-white" />
                Create Chat Room
              </h3>
              <button onClick={() => setShowCreateModal(false)} className="text-neutral-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Room Name *</label>
                <Input
                  value={newRoom.name}
                  onChange={(e) => setNewRoom(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="e.g., AI Tools Community"
                  className="bg-neutral-800 border-white/10"
                  maxLength={50}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Input
                  value={newRoom.description}
                  onChange={(e) => setNewRoom(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="What's this room about?"
                  className="bg-neutral-800 border-white/10"
                  maxLength={200}
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Room Rules * <span className="text-neutral-400 font-normal">(min 20 characters)</span>
                </label>
                <Textarea
                  value={newRoom.rules}
                  onChange={(e) => setNewRoom(prev => ({ ...prev, rules: e.target.value }))}
                  placeholder="1. Be respectful&#10;2. No spam&#10;3. Stay on topic..."
                  className="bg-neutral-800 border-white/10"
                  rows={5}
                />
                <p className="text-xs text-neutral-500 mt-1">{newRoom.rules.length}/20 minimum</p>
              </div>

              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_private"
                  checked={newRoom.is_private}
                  onChange={(e) => setNewRoom(prev => ({ ...prev, is_private: e.target.checked }))}
                  className="rounded border-white/20 bg-neutral-800"
                />
                <label htmlFor="is_private" className="text-sm">
                  Make this room private (invite only)
                </label>
              </div>

              <div className="pt-4 flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setShowCreateModal(false)}
                  className="flex-1 border-white/20"
                >
                  Cancel
                </Button>
                <Button
                  onClick={createRoom}
                  disabled={creating || !newRoom.name.trim() || newRoom.rules.length < 20}
                  className="flex-1 bg-white text-black hover:bg-neutral-200"
                >
                  {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                  Create Room
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Room Rules Acceptance Modal */}
      {showRulesModal && pendingRoomJoin && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-neutral-900 border border-white/10 rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                pendingRoomJoin.room_type === 'creator_lounge' ? 'bg-white text-black/20' :
                pendingRoomJoin.room_type === 'deals' ? 'bg-[#00FF94]/20' :
                'bg-[#00F0FF]/20'
              }`}>
                {getRoomIcon(pendingRoomJoin)}
              </div>
              <div>
                <h3 className="text-xl font-bold">{pendingRoomJoin.name}</h3>
                <p className="text-sm text-neutral-400">Please read and accept the room rules</p>
              </div>
            </div>

            {pendingRoomJoin.description && (
              <p className="text-neutral-300 mb-4">{pendingRoomJoin.description}</p>
            )}

            <div className="bg-neutral-800/80 border border-white/10 rounded-xl p-4 mb-6">
              <h4 className="font-semibold mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-[#00F0FF]" />
                Room Rules
              </h4>
              <div className="text-sm text-neutral-300 whitespace-pre-wrap">
                {pendingRoomJoin.rules}
              </div>
            </div>

            <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6">
              <p className="text-sm text-yellow-200">
                <strong>⚠️ Important:</strong> By joining this room, you agree to follow these rules. 
                Violations may result in removal from the room or chat ban.
              </p>
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={cancelJoinRoom}
                className="flex-1 border-white/20"
                disabled={joiningRoom}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmJoinWithRules}
                className="flex-1 bg-[#00F0FF] hover:bg-[#00C2CC] text-black"
                disabled={joiningRoom}
              >
                {joiningRoom ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Check className="w-4 h-4 mr-2" />
                )}
                I Accept & Join
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatRooms;
